import React from "react";
import { Subscription } from "rxjs";
import ConfigProps from "../../constants/configProps";
import NavigationTokens from "../../constants/navigationTokens";
import ConfigPropService from "../../utilities/configPropService";
import { ContactsService } from "../../utilities/contactsService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import { PhoneService } from "../../utilities/phoneService";
import SessionCacheService from "../../utilities/sessionCacheService";
import MenuItem from "../navMenu/menuItem";
import Skeleton from "../skeleton/skeleton";
import "./yourRepresentative.scss";
import environmentService from "../../utilities/environmentService";

const YourRepIcon = process.env.PUBLIC_URL + "/images/your-rep-icon.png";
const STORAGE_KEY_CUSTOMER_SERVICE = "PrimaryCustomerServiceRep";

export const RepTypes = {
  customerService: "Client Relations Specialist",
  accountsReceivable: "Accounts Receivable Representative",
};

export interface IYourRepresentativeProps {
  repType: string;
  isDesktop?: boolean;
}

export interface IYourRepresentativeState {
  name: string;
  email: string;
  phone: string;
  extension: string;
  telHref: string;
  customerServiceHref: string;
  defaultArRepImage: string;
  src: string;
  primaryCsrLoaded?: boolean;
}

class YourRepresentative extends React.Component<
  IYourRepresentativeProps,
  IYourRepresentativeState
> {
  constructor(props: IYourRepresentativeProps) {
    super(props);

    if (props.repType === RepTypes.customerService) {
      const primaryCsr = SessionCacheService.getValue(
        STORAGE_KEY_CUSTOMER_SERVICE
      );

      if (primaryCsr) {
        this.state = {
          name: primaryCsr.name,
          email: primaryCsr.email,
          phone: primaryCsr.phone,
          extension: primaryCsr.extension,
          telHref: primaryCsr.telHref,
          customerServiceHref: "",
          defaultArRepImage: "",
          src: primaryCsr.src,
          primaryCsrLoaded: true,
        };
      }
    } else {
      this.state = {
        name: "",
        email: "",
        phone: "",
        extension: "",
        telHref: "",
        customerServiceHref: "",
        defaultArRepImage: "",
        src: "",
      };
    }
  }

  getTelHref(phone: string, ext: string) {
    if (ext) {
      return `tel:${phone};ext=${ext}`;
    }

    return `tel:${phone}`;
  }

  navTokenSub: Subscription | undefined;
  configPropSub: Subscription | undefined;
  customerServiceSub: Subscription | undefined;
  arContactSub: Subscription | undefined;

  componentWillUnmount() {
    if (this.navTokenSub) {
      this.navTokenSub.unsubscribe();
    }

    if (this.configPropSub) {
      this.configPropSub.unsubscribe();
    }

    if (this.customerServiceSub) {
      this.customerServiceSub.unsubscribe();
    }

    if (this.arContactSub) {
      this.arContactSub.unsubscribe();
    }
  }

  componentDidMount() {
    let queryString = "";

    if (this.props.repType === RepTypes.customerService) {
      queryString = "?cntp=0";
    }

    this.navTokenSub = NavigationTokensService.get([
      NavigationTokens.customerService,
    ]).subscribe((navTokens) => {
      this.setState({
        customerServiceHref:
          navTokens[NavigationTokens.customerService] + queryString,
      });
    });

    this.configPropSub = ConfigPropService.get([
      ConfigProps.CustomerServicePhoneNumber,
      ConfigProps.DefaultARRepImage,
    ]).subscribe((configProps) => {
      const primaryCsr = SessionCacheService.getValue(
        STORAGE_KEY_CUSTOMER_SERVICE
      );
      if (this.props.repType === RepTypes.customerService && !primaryCsr) {
        this.setState({
          phone: configProps[ConfigProps.CustomerServicePhoneNumber],
        });
      }

      this.setState(
        {
          defaultArRepImage: configProps[ConfigProps.DefaultARRepImage],
        },
        () => {
          if (this.props.repType === RepTypes.accountsReceivable) {
            this.arContactSub = ContactsService.getArContact().subscribe(
              (arInfo) => {
                this.setState({
                  name: arInfo.name,
                  email: arInfo.email,
                  extension: arInfo.phone,
                  telHref: this.getTelHref(
                    PhoneService.normalizePhone(this.state.phone),
                    arInfo.phone
                  ),
                  src: arInfo.imageUrl,
                });
              }
            );
          }

          const primaryCsr = SessionCacheService.getValue(
            STORAGE_KEY_CUSTOMER_SERVICE
          );

          if (this.props.repType === RepTypes.customerService && !primaryCsr) {
            this.customerServiceSub =
              ContactsService.getCustomerService().subscribe(
                (contactInfo) => {
                  const contact = contactInfo.contacts.filter((ci) => {
                    return ci.contactType === "PrimaryCSR";
                  })[0];

                  let phone = contact?.phone || this.state.phone;
                  let extension = contact?.extension || this.state.extension;

                  if (
                    environmentService.get().isEcom &&
                    phone &&
                    phone.indexOf(" ext. ") !== -1
                  ) {
                    let tokens = phone.split(" ext. ");

                    phone = tokens[0];
                    extension = tokens[1];
                  }

                  const primaryCsr = {
                    name: contact?.name,
                    phone: phone,
                    extension: extension,
                    email: contact?.email,
                    src: contact?.imageURL,
                    telHref: this.getTelHref(
                      PhoneService.normalizePhone(phone),
                      extension
                    ),
                  };

                  SessionCacheService.set(
                    STORAGE_KEY_CUSTOMER_SERVICE,
                    primaryCsr
                  );

                  this.setState({ ...primaryCsr, primaryCsrLoaded: true });
                },
                () => {
                  this.customerServiceSub?.unsubscribe();
                }
              );
          }
        }
      );
    });
  }

  renderImage() {
    let src = YourRepIcon;

    if (this.state.src) {
      src = this.state.src;
    } else if (
      this.props.repType === RepTypes.accountsReceivable &&
      this.state.defaultArRepImage
    ) {
      src = this.state.defaultArRepImage;
    }

    return (
      <img
        src={src}
        onClick={() => {
          window.location.href = this.state.customerServiceHref;
        }}
        alt="Your Representative"
      ></img>
    );
  }

  render() {
    return (
      <>
        {this.state &&
        (this.state.primaryCsrLoaded ||
          !(this.props.repType === RepTypes.customerService)) ? (
          <>
            <MenuItem
              src={YourRepIcon}
              label={`Your ${this.props.repType}`}
              linkClass={`${
                this.props.isDesktop ? "no-hover" : "no-link-style"
              } ${this.state.customerServiceHref ? "" : "inactive"}`}
              srcClass="rep-icon"
              href={this.state.customerServiceHref}
            />
            <div className="rep-info-container form-group">
              <a
                href={this.state.customerServiceHref}
                className={`${
                  this.props.isDesktop
                    ? "img-position "
                    : "img-position no-link-style"
                } ${this.state.customerServiceHref ? "" : "inactive"}`}
              >
                {this.renderImage()}
              </a>
              <div className="font-weight-bold">
                <a
                  href={
                    this.props.isDesktop
                      ? this.state.customerServiceHref
                      : undefined
                  }
                  className={`
                    ${
                      this.props.isDesktop ? "a-no-link-style" : "no-link-style"
                    } ${this.state.customerServiceHref ? "" : "inactive"}`}
                >
                  {this.state.name}
                </a>
              </div>
              {this.state.phone && !this.state.extension && (
                <div>
                  <span>{this.state.phone}</span>
                </div>
              )}
              {this.state.phone && this.state.extension && (
                <div>
                  <span>Ext. {this.state.extension}</span>
                </div>
              )}
              {this.state.email && (
                <div>
                  <a
                    href={
                      this.props.isDesktop
                        ? this.state.customerServiceHref
                        : undefined
                    }
                    className={`${
                      this.props.isDesktop ? " " : "no-link-style"
                    } ${this.state.customerServiceHref ? "" : "inactive"}`}
                  >
                    {this.state.email}
                  </a>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <Skeleton style={{ height: 30, width: 200, marginTop: 10 }} />
            <Skeleton style={{ height: 30, width: 200, marginTop: 10 }} />
            <Skeleton
              style={{
                height: 40,
                width: 200,
                marginTop: 10,
                marginBottom: 20,
              }}
            />
          </>
        )}
      </>
    );
  }
}

export default YourRepresentative;
